import { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { fmtHonorarioEur } from './honorario';
import { useServiceProducts, useUpdateServiceProduct } from './hooks/useServiceProducts';
import type { ServiceProduct, ServiceProductKey } from './types';

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

function eurosToCents(value: string): number | null {
  const n = parseFloat(value.replace(',', '.'));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function centsToEuroDraft(cents: number): string {
  return (cents / 100).toFixed(2);
}

type ConfirmState =
  | { kind: 'price'; product: ServiceProduct; nextCents: number }
  | { kind: 'disable'; product: ServiceProduct }
  | { kind: 'enable'; product: ServiceProduct }
  | null;

export function GestionesPage() {
  const { data: products, isLoading, isFetching, refetch } = useServiceProducts();
  const update = useUpdateServiceProduct();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [openHistory, setOpenHistory] = useState<ServiceProductKey | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [disableReason, setDisableReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!products?.length) return;
    setDrafts((prev) => {
      const next = { ...prev };
      for (const p of products) {
        if (next[p.key] === undefined) next[p.key] = centsToEuroDraft(p.serviceFeeCents);
      }
      return next;
    });
  }, [products]);

  function requestPriceSave(product: ServiceProduct) {
    const cents = eurosToCents(drafts[product.key] ?? '');
    if (cents == null || cents > 20000) {
      setError('El honorario debe estar entre 0 y 200 €.');
      return;
    }
    if (cents === product.serviceFeeCents) return;
    setError(null);
    setConfirm({ kind: 'price', product, nextCents: cents });
  }

  async function applyConfirm() {
    if (!confirm) return;
    setError(null);
    try {
      if (confirm.kind === 'price') {
        await update.mutateAsync({
          key: confirm.product.key,
          body: { serviceFeeCents: confirm.nextCents },
        });
      } else if (confirm.kind === 'disable') {
        const reason = disableReason.trim();
        if (!reason) {
          setError('Indica el motivo que verá el usuario.');
          return;
        }
        await update.mutateAsync({
          key: confirm.product.key,
          body: { isEnabled: false, disabledReason: reason },
        });
      } else {
        await update.mutateAsync({
          key: confirm.product.key,
          body: { isEnabled: true, disabledReason: null },
        });
      }
      setConfirm(null);
      setDisableReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Gestiones</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Honorario e interruptor de cada trámite. El cambio aplica a pedidos nuevos; los ya
            creados conservan el importe que se les cobró.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground bg-muted/50"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-sm text-rose-400">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : (
        <div className="space-y-3">
          {(products || []).map((p) => {
            const historyOpen = openHistory === p.key;
            return (
              <div key={p.key} className="rounded-xl border border-border overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-muted/30 border-b border-border">
                  <div>
                    <div className="font-semibold">{p.displayName}</div>
                    <div className="text-xs text-muted-foreground font-mono">{p.key}</div>
                  </div>
                  <span
                    className={cn(
                      'inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium',
                      p.isEnabled
                        ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'
                        : 'bg-rose-500/10 text-rose-400 border-rose-500/30'
                    )}
                  >
                    {p.isEnabled ? 'Activa' : 'Inactiva'}
                  </span>
                </div>
                <div className="p-4 space-y-4">
                  <div className="flex flex-wrap items-end gap-3">
                    <label className="space-y-1">
                      <span className="text-xs text-muted-foreground">Honorario (IVA incl.)</span>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={drafts[p.key] ?? ''}
                          onChange={(e) =>
                            setDrafts((prev) => ({ ...prev, [p.key]: e.target.value }))
                          }
                          className="w-28 h-10 rounded-md border border-input bg-background px-3 text-sm"
                        />
                        <span className="text-sm text-muted-foreground">€</span>
                      </div>
                    </label>
                    <button
                      type="button"
                      disabled={update.isPending}
                      onClick={() => requestPriceSave(p)}
                      className="h-10 px-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                    >
                      Guardar precio
                    </button>
                    <button
                      type="button"
                      disabled={update.isPending}
                      onClick={() => {
                        setError(null);
                        setDisableReason('');
                        setConfirm(
                          p.isEnabled
                            ? { kind: 'disable', product: p }
                            : { kind: 'enable', product: p }
                        );
                      }}
                      className={cn(
                        'h-10 px-3 rounded-lg text-sm font-medium border',
                        p.isEnabled
                          ? 'border-rose-500/30 text-rose-400 hover:bg-rose-500/10'
                          : 'border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10'
                      )}
                    >
                      {p.isEnabled ? 'Desactivar' : 'Activar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpenHistory((cur) => (cur === p.key ? null : p.key))}
                      className="h-10 px-3 rounded-lg text-sm text-muted-foreground hover:text-foreground bg-muted/50"
                    >
                      {historyOpen ? 'Ocultar histórico' : 'Ver histórico'}
                    </button>
                  </div>
                  {!p.isEnabled && p.disabledReason && (
                    <p className="text-sm text-rose-400">Motivo: {p.disabledReason}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Último cambio: {fmtDate(p.updatedAt)}
                    {p.updatedBy ? ` · ${p.updatedBy}` : ''}
                    {` · ahora ${fmtHonorarioEur(p.serviceFeeCents)}`}
                  </p>
                  {historyOpen && (
                    <div className="rounded-lg border border-border overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs text-muted-foreground">
                          <tr>
                            <th className="text-left font-medium px-3 py-2">Fecha</th>
                            <th className="text-left font-medium px-3 py-2">Honorario</th>
                            <th className="text-left font-medium px-3 py-2">Autor</th>
                            <th className="text-left font-medium px-3 py-2">Nota</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(p.history || []).length === 0 ? (
                            <tr>
                              <td colSpan={4} className="px-3 py-3 text-muted-foreground">
                                Sin histórico
                              </td>
                            </tr>
                          ) : (
                            (p.history || []).map((h, i) => (
                              <tr key={`${h.changedAt}-${i}`} className="border-t border-border">
                                <td className="px-3 py-2 whitespace-nowrap">{fmtDate(h.changedAt)}</td>
                                <td className="px-3 py-2">{fmtHonorarioEur(h.serviceFeeCents)}</td>
                                <td className="px-3 py-2 font-mono text-xs">{h.changedBy || '—'}</td>
                                <td className="px-3 py-2 text-muted-foreground">{h.note || '—'}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AdminConfirmDialog
        open={confirm != null}
        title={
          confirm?.kind === 'price'
            ? 'Cambiar honorario'
            : confirm?.kind === 'disable'
              ? 'Desactivar gestión'
              : 'Activar gestión'
        }
        variant={confirm?.kind === 'disable' ? 'danger' : 'default'}
        confirmDisabled={update.isPending}
        confirmLabel={update.isPending ? 'Guardando…' : 'Confirmar'}
        description={
          confirm?.kind === 'price' ? (
            <p>
              Vas a dejar el honorario de <strong>{confirm.product.displayName}</strong> en{' '}
              <strong>{fmtHonorarioEur(confirm.nextCents)}</strong>. Esto cambia el precio de los
              pedidos nuevos. Los pedidos ya creados mantienen el suyo.
            </p>
          ) : confirm?.kind === 'disable' ? (
            <div className="space-y-3">
              <p>
                Se quita el checkout de <strong>{confirm.product.displayName}</strong>. El contenido
                informativo sigue publicado.
              </p>
              <label className="block space-y-1">
                <span className="text-xs">Motivo que verá el usuario</span>
                <textarea
                  value={disableReason}
                  onChange={(e) => setDisableReason(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Por ejemplo: cerrado por temporada."
                />
              </label>
            </div>
          ) : confirm ? (
            <p>
              Se vuelve a habilitar el checkout de <strong>{confirm.product.displayName}</strong>.
            </p>
          ) : null
        }
        onConfirm={() => void applyConfirm()}
        onCancel={() => {
          setConfirm(null);
          setDisableReason('');
        }}
      />
    </div>
  );
}
